<template>
  <div
    class="generic-grid-block"
    :class="{
      'generic-grid-block--clickable': isClickable,
      'generic-grid-block--selected': isSelected
    }"
    :style="selectedStyle"
    @click="onCardClick"
    @dblclick="onCardDoubleClick"
  >
    <div
      class="generic-grid-block__row generic-grid-block__header"
    >
      <div
        class="generic-grid-block__cell generic-grid-block__title"
      >
        <!-- The same tick the table's rows carry, reading the same state - a record
             picked in one view is picked in the other. It stops the click itself, so
             ticking a card is not also a click on it. -->
        <table-row-checkbox
          v-if="canSelectRows"
          :selection-state="rowsSelectionState"
          :row-key="rowSelectionKey"
          @change="onCheckboxChange"
        />
        <div
          class="generic-grid-block__title-wrapper"
          :title="titleText"
        >
          {{ titleText }}
        </div>
      </div>

      <!-- The actions are about the record rather than about opening it, so a click
           that lands here is not also a click on the card - the same thing the table's
           actions column does. -->
      <div
        class="generic-grid-block__cell generic-grid-block__actions"
        @click.stop
      >
        <template
          v-if="topRightComponent"
        >
          <component
            :is="topRightComponent"
            :config="topRightComponentConfig"
            :data="data"
            :full-data="fullData"
            :item="item"
            :item-index="itemIndex"
            :main-config="mainConfig"
            :grid-config="gridConfig"
            :block-config="blockConfig"
          />
        </template>
        <template
          v-else-if="hasActions"
        >
          <table-row-actions-menu
            :data-index="itemIndex"
            :config="actionsMenuConfig"
            :table-data="data"
            :permissions="actionsPermissions"
            :pagination="actionsPagination"
            :fetch-table-data="resolvedFetchTableData"
            :reset-selections="actionsResetSelections"
            :show-table-details-page="actionsShowTableDetailsPage"
          />
        </template>
      </div>
    </div>

    <div
      v-for="field in visibleFieldRows"
      :key="field.rowKey"
      class="generic-grid-block__row"
    >
      <div
        class="generic-grid-block__cell generic-grid-block__cell--half data-label"
      >
        <div
          class="truncate-text"
        >
          {{ field.label }}
        </div>
      </div>

      <div
        class="generic-grid-block__cell generic-grid-block__cell--half data-value"
      >
        <div
          class="truncate-text"
        >
          <cell-content
            v-if="field.usesCellContent"
            :cell-type="field.cellType"
            :cell-data="field.cellData"
            :header="field.header"
            :row-data="item"
            :row-index="itemIndex"
            :fetch-table-data="resolvedFetchTableData"
            :cells-config="cellsConfig"
            :header-cell-config="field.headerCellConfig"
            :global-config="globalConfig"
          />
          <template
            v-else
          >
            {{ field.textValue }}
          </template>
        </div>
      </div>
    </div>

    <template
      v-if="isExpanded"
    >
      <div
        v-for="field in hiddenFieldRows"
        :key="field.rowKey"
        class="generic-grid-block__row"
      >
        <div
          class="generic-grid-block__cell generic-grid-block__cell--half data-label"
        >
          <div
            class="truncate-text"
          >
            {{ field.label }}
          </div>
        </div>

        <div
          class="generic-grid-block__cell generic-grid-block__cell--half data-value"
        >
          <div
            class="truncate-text"
          >
            <cell-content
              v-if="field.usesCellContent"
              :cell-type="field.cellType"
              :cell-data="field.cellData"
              :header="field.header"
              :row-data="item"
              :row-index="itemIndex"
              :fetch-table-data="resolvedFetchTableData"
              :cells-config="cellsConfig"
              :header-cell-config="field.headerCellConfig"
              :global-config="globalConfig"
            />
            <template
              v-else
            >
              {{ field.textValue }}
            </template>
          </div>
        </div>
      </div>

      <!-- The table's row expansion - see `rowExpansion` in the table config - drawn
           under the fields, so a listing whose rows open up does so on a card as well.
           Told it is `compact`: a card is a phone's width, or a quarter of a desktop.
           A click inside it is the expansion's own, not a click on the card. -->
      <div
        v-if="hasRowExpansion"
        class="generic-grid-block__expansion"
        @click.stop
        @dblclick.stop
      >
        <component
          :is="rowExpansionConfig.component"
          v-bind="rowExpansionProps"
          compact
        />
      </div>
    </template>

    <!-- Showing the rest of the record is not opening it either. -->
    <span
      v-if="canToggle"
      @click.stop
    >
      <image-field
        :src="isExpanded ? '/img/icons/arrow-up-base.svg' : '/img/icons/arrow-down-base.svg'"
        class="arrowUpDown"
        @onClick="toggleExpanded"
      />
    </span>
  </div>
</template>

<script>
import ImageField from '@/global/components/fields/ImageField.vue'
import CellContent from '@/global/components/table/components/TableCellContent.vue'
import TableRowActionsMenu from '@/global/components/table/components/TableRowActionsMenu.vue'
import TableRowCheckbox from '@/global/components/table/components/TableRowCheckbox.vue'
import { isFunction, isObject } from '@/global/utils/utils'
import { getOpenRecordHandler } from '@/global/composables/table/useTableService'

const DEFAULT_EMPTY_VALUE = '--'
const DEFAULT_VISIBLE_FIELDS_COUNT = 4
// The cell type of a column that is simply written out - anything else is drawn by a
// component and has to go through CellContent.
const PLAIN_CELL_TYPE = 'base'
const NO_OP = () => {}
// How long a single click waits to find out whether it was the first half of a double
// click. Only paid on a card that has something for a double click to do.
const CLICK_DELAY = 250
// How far apart two clicks on the same card may be and still turn out to be a double
// click. What the browsers themselves allow between the two.
const DOUBLE_CLICK_WINDOW = 500
// Values a cell holds that are nothing to show - kept as one frozen array rather than
// built per cell: a grid of any length asks for this thousands of times over.
const EMPTY_CELL_VALUES = Object.freeze([undefined, null, ''])

export default {
  name: 'GenericGridBlock',

  components: {
    ImageField,
    TableRowActionsMenu,
    TableRowCheckbox,
    CellContent
  },

  // What acting on the record needs - the fetch that refreshes the listing after a
  // record is deleted, the permissions deciding which actions a user is offered, what a
  // click on the card is handed. The table hands these to its rows as props; here they
  // come from DataTable itself, because the grid component in between is one a screen
  // may replace with its own and cannot be asked to pass them on. Defaulted, so this
  // block still works when it is used outside a table.
  inject: {
    tableRowContext: {
      default: null
    }
  },

  props: {
    data: {
      type: Array,
      default: () => ([])
    },
    fullData: {
      type: [Array, Object],
      default: () => ([])
    },
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    itemIndex: {
      type: Number,
      default: 0
    },
    mainConfig: {
      type: Object,
      default: () => ({})
    },
    gridConfig: {
      type: Object,
      default: () => ({})
    },
    blockConfig: {
      type: Object,
      default: () => ({})
    },
    fetchTableData: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      isExpanded: false,
      clickTimeout: null,
      // What the card was before a double click may have started on it - see
      // `rememberStateBeforeDoubleClick`.
      clickedBeforeDoubleClick: null
    }
  },

  computed: {
    tableConfig () {
      return this.mainConfig?.tableConfig?.tableConfig || this.mainConfig?.tableConfig || {}
    },

    tableFields () {
      return this.normalizeFieldList(this.tableConfig?.headers || [], [])
    },

    cellsConfig () {
      return this.tableConfig?.cellsConfig || {}
    },

    globalConfig () {
      return this.tableConfig?.globalConfig || {}
    },

    normalizedFields () {
      const sourceFields = this.tableFields
      const configuredFields = this.blockConfig?.fields || this.gridConfig?.fields || []
      const blockFields = this.normalizeFieldList(configuredFields, sourceFields)
      const fields = blockFields.length ? blockFields : this.tableFields
      const excludedKeys = Array.isArray(this.blockConfig?.excludeKeys)
        ? this.blockConfig.excludeKeys
        : Array.isArray(this.gridConfig?.excludeKeys)
          ? this.gridConfig.excludeKeys
          : []

      return fields.filter(field => {
        const key = field?.key
        return key ? !excludedKeys.includes(key) : true
      })
    },

    titleField () {
      const titleKey = this.blockConfig?.titleKey || this.gridConfig?.titleKey
      if (titleKey) {
        return this.normalizedFields.find(field => field?.key === titleKey) || null
      }

      const titleField = this.normalizedFields[0]
      return titleField || null
    },

    titleText () {
      const titleSource = this.blockConfig?.title !== undefined
        ? this.blockConfig
        : (this.gridConfig || {})
      const { title } = titleSource

      if (isFunction(title)) {
        const resolvedTitle = title({
          data: this.item,
          rowData: this.item,
          item: this.item,
          itemIndex: this.itemIndex
        })

        return this.normalizeTextValue(resolvedTitle)
      }

      if (typeof title === 'string') {
        return title
      }

      return this.getFieldTextValue(this.titleField)
    },

    contentFields () {
      const titleKey = this.titleField?.key
      const fields = this.normalizedFields.filter(field => field?.key !== titleKey)

      const configuredContentFields = this.blockConfig?.contentFields || this.gridConfig?.contentFields
      if (Array.isArray(configuredContentFields) && configuredContentFields.length) {
        return this.normalizeFieldList(configuredContentFields, this.tableFields)
      }

      return fields
    },

    visibleFieldsCount () {
      return this.blockConfig?.visibleFieldsCount ?? this.gridConfig?.visibleFieldsCount ?? DEFAULT_VISIBLE_FIELDS_COUNT
    },

    visibleFields () {
      const configuredVisibleFields = this.blockConfig?.visibleFields || this.gridConfig?.visibleFields
      if (Array.isArray(configuredVisibleFields) && configuredVisibleFields.length) {
        return this.normalizeFieldList(configuredVisibleFields, this.tableFields)
      }

      return this.contentFields.slice(0, this.visibleFieldsCount)
    },

    hiddenFields () {
      const configuredHiddenFields = this.blockConfig?.hiddenFields || this.gridConfig?.hiddenFields
      if (Array.isArray(configuredHiddenFields) && configuredHiddenFields.length) {
        return this.normalizeFieldList(configuredHiddenFields, this.tableFields)
      }

      return this.contentFields.slice(this.visibleFieldsCount)
    },

    /**
     * The visible fields with everything the template needs already worked out.
     *
     * Resolved here rather than by calling methods from the markup: a method in a
     * template is run again on every render of every card, and the cell type, the cell
     * config and the displayed value are all lookups that do not change unless the
     * record or the config does. As a computed they are worked out once and then simply
     * read - which is what a grid of thousands of records notices.
     */
    visibleFieldRows () {
      return this.buildFieldRows(this.visibleFields, 'visible')
    },

    // Only ever read while the card is expanded, so a collapsed card never pays for it.
    hiddenFieldRows () {
      return this.buildFieldRows(this.hiddenFields, 'hidden')
    },

    canToggle () {
      const collapsible = this.blockConfig?.collapsible ?? this.gridConfig?.collapsible ?? true
      return collapsible !== false && (this.hiddenFields.length > 0 || this.hasRowExpansion)
    },

    // The same `rowExpansion` the table's rows read - see `rowExpansionConfig` in
    // TableBody - so a record that opens up in one view opens up in the other.
    rowExpansionConfig () {
      return this.tableConfig?.rowExpansion || null
    },

    hasRowExpansion () {
      return this.tableHasRowExpansion && !!this.rowExpansionConfig.isExpandable(this.item)
    },

    // Whether the records of this listing open up at all - whether or not this one does.
    tableHasRowExpansion () {
      const { isExpandable, component } = this.rowExpansionConfig || {}
      return !!component && isFunction(isExpandable)
    },

    /**
     * Whether a click ticks the card by itself. Not on a listing whose records open up:
     * there the tick is the opening's (see `syncSelectionWithExpansion`), so a card with
     * nothing to open is never ticked by a click - the same rule as the table's rows.
     */
    ticksOnClick () {
      return this.selectsOnClick && !this.tableHasRowExpansion
    },

    rowExpansionProps () {
      const { props } = this.rowExpansionConfig || {}
      return isFunction(props) ? props(this.item) : (props || {})
    },

    /**
     * The row actions this card offers.
     *
     * A grid config may name its own, and otherwise the card shows the ones the table
     * shows - the same actions on the same record, which is what a view switch is
     * supposed to leave alone. `displayActions` is honoured either way: a table that
     * hides its actions column - while records are being ticked, say - hides them here
     * too.
     */
    resolvedActionsConfig () {
      const { displayActions = true } = this.tableConfig || {}

      if (!displayActions) {
        return []
      }

      const configuredActions = this.blockConfig?.actionsConfig || this.gridConfig?.actionsConfig

      if (Array.isArray(configuredActions)) {
        return configuredActions
      }

      const tableActions = this.tableConfig?.actionsConfig

      return Array.isArray(tableActions) ? tableActions : []
    },

    hasActions () {
      return this.resolvedActionsConfig.length > 0
    },

    actionsMenuConfig () {
      return {
        tableConfig: {
          actionsConfig: this.resolvedActionsConfig
        }
      }
    },

    actionsPermissions () {
      return this.tableRowContext?.getPermissions() ?? null
    },

    actionsPagination () {
      return this.tableRowContext?.getPagination() ?? {}
    },

    // The listing's own fetch where there is one - an action that deletes a record, or
    // a cell that changes one, has to be able to ask for what is left. The
    // `fetchTableData` prop stands in when this block is used outside a table.
    resolvedFetchTableData () {
      return this.tableRowContext?.getFetchTableData() ?? this.fetchTableData
    },

    actionsResetSelections () {
      return this.tableRowContext?.getResetSelections() ?? NO_OP
    },

    actionsShowTableDetailsPage () {
      return this.tableRowContext?.getShowTableDetailsPage() ?? NO_OP
    },

    // The card reacts to a click when there is a record to mark, more of it to show, or
    // something for opening it to do.
    isClickable () {
      return this.ticksOnClick || this.canToggle || !!this.openRecord
    },

    /**
     * What opens the record.
     *
     * The screen's `rowDblClick`, read the way the table's rows read it - see
     * `getOpenRecordHandler`. Either way it is the card's double click.
     */
    openRecord () {
      return getOpenRecordHandler(this.mainConfig)
    },

    // The colour is only put on the card that wears it, so nothing else re-renders when
    // a tick moves.
    selectedStyle () {
      if (!this.isSelected) {
        return null
      }

      return {
        '--selected-grid-card-color': this.tableRowContext?.getSelectedRowColor()
      }
    },

    canSelectRows () {
      return !!this.tableRowContext?.canSelectRows()
    },

    // Ticking, the way the whole application does it: a click ticks the record and a
    // double click opens it. Read off the table's own config so a card and a row of the
    // same listing cannot behave differently.
    rowsSelectionConfig () {
      return this.tableRowContext?.getRowsSelectionConfig() ?? {}
    },

    selectsOnClick () {
      return this.rowsSelectionConfig.selectOnRowClick !== false
    },

    isSelected () {
      return !!this.rowsSelectionState[this.rowSelectionKey]
    },

    rowsSelectionState () {
      return this.tableRowContext?.getRowsSelectionState() ?? {}
    },

    // Keyed by whatever the table keys its selection by - the record's id where it has
    // been asked for, its place in the listing otherwise. Worked out by DataTable so
    // the two views cannot disagree about which record is ticked.
    rowSelectionKey () {
      return this.tableRowContext?.getRowSelectionKey(this.item, this.itemIndex) ?? this.itemIndex
    },

    topRightComponent () {
      return this.blockConfig?.topRightComponent?.component || this.gridConfig?.topRightComponent?.component || null
    },

    topRightComponentConfig () {
      return this.blockConfig?.topRightComponent || this.gridConfig?.topRightComponent || null
    }
  },

  watch: {
    // The grid keys its cards by their place in the listing, so a fresh set of records
    // lands in the cards already drawn - and a card left open would open the new record.
    item (item, previousItem) {
      if (item !== previousItem) {
        this.isExpanded = false
      }
    },

    // The grid is told, because an open card is taller than the rest - see
    // `expandedBlocks` in GridView.
    isExpanded (isExpanded) {
      this.$emit('expansion-change', isExpanded)
    }
  },

  // The unfold is delayed (see `onCardClick`), so a click followed by leaving the screen
  // within that quarter of a second would otherwise fire into a card that is gone.
  beforeDestroy () {
    clearTimeout(this.clickTimeout)

    // A card going away open is one the grid should stop counting.
    if (this.isExpanded) {
      this.$emit('expansion-change', false)
    }
  },

  methods: {
    /**
     * What a plain click on the card does.
     *
     * It ticks the record, the same as a click on the row does in the table - a card
     * picked in one view is picked in the other. Where nothing is ticked the click is
     * free to show the rest of the record instead; on a listing that does both,
     * unfolding stays on the arrow at the foot of the card.
     *
     * The unfolding is held back for a moment where a double click opens the record,
     * because the two clicks of a double click both land here first - and the card
     * would otherwise unfold and fold again on the way to the details.
     */
    onCardClick () {
      clearTimeout(this.clickTimeout)

      // A click that ends a selection is the user picking text out of the card, not
      // asking for anything - the table reads a click the same way.
      if (window.getSelection().toString()) {
        return
      }

      // Ticking happens on the click itself rather than a quarter of a second behind
      // it: the two clicks of a double click tick and untick the card, leaving it as
      // the user found it, so there is nothing for the delay to save.
      // A card that opens up is ticked along with opening it instead - see
      // `syncSelectionWithExpansion`.
      if (this.selectsOnClick) {
        this.rememberStateBeforeDoubleClick()

        if (this.ticksOnClick) {
          this.onCheckboxChange(!this.isSelected)
        }
      }

      const runSingleClick = this.canToggle ? this.toggleExpanded : null

      if (!runSingleClick) {
        return
      }

      if (!this.openRecord) {
        runSingleClick()

        return
      }

      this.clickTimeout = setTimeout(runSingleClick, CLICK_DELAY)
    },

    toggleExpanded () {
      this.isExpanded = !this.isExpanded
      this.syncSelectionWithExpansion()
    },

    /**
     * On a card that opens up, the tick follows the opening: an open card is ticked and
     * a closed one is not - the same rule the table's rows go by, see
     * `syncSelectionWithExpansion` in TableBody. Ticked and opened separately, the two
     * fell out of step, and a card closed again was left ticked.
     */
    syncSelectionWithExpansion () {
      if (!this.selectsOnClick || !this.hasRowExpansion) {
        return
      }

      if (this.isSelected !== this.isExpanded) {
        this.onCheckboxChange(this.isExpanded)
      }
    },

    /**
     * Opens the record the card stands for.
     *
     * Handed the same thing the table hands its own handler, so a screen does not have
     * to know which of the two views the click came from - including `isRowMarked`, the
     * state the click started from, which a listing that marks acts on.
     */
    /**
     * Holds on to what the card was before a double click might have started on it -
     * see the row's own handler in TableBody for why the two clicks cannot be left to
     * cancel each other out.
     */
    rememberStateBeforeDoubleClick () {
      const previous = this.clickedBeforeDoubleClick
      const now = Date.now()

      if (previous && (now - previous.at) <= DOUBLE_CLICK_WINDOW) {
        previous.at = now

        return
      }

      this.clickedBeforeDoubleClick = {
        wasSelected: this.isSelected,
        at: now
      }
    },

    // Puts the card back the way the double click found it.
    restoreStateBeforeDoubleClick () {
      const before = this.clickedBeforeDoubleClick

      this.clickedBeforeDoubleClick = null

      if (!before || this.isSelected === before.wasSelected) {
        return
      }

      this.onCheckboxChange(before.wasSelected)
    },

    onCardDoubleClick () {
      clearTimeout(this.clickTimeout)

      // Opening the record is not a way of ticking it, so the card is left as it was
      // before the two clicks that opened it.
      this.restoreStateBeforeDoubleClick()

      if (!this.openRecord) {
        return
      }

      this.openRecord({
        index: this.itemIndex,
        data: this.item,
        instance: this.tableRowContext?.getOwnerInstance() ?? null,
        showTableDetailsPage: this.actionsShowTableDetailsPage,
        pagination: this.actionsPagination,
        isRowSelected: this.isSelected
      })
    },

    onCheckboxChange (selectionState) {
      clearTimeout(this.clickTimeout)

      this.tableRowContext?.selectRow({
        selectionState,
        data: this.item,
        index: this.itemIndex
      })
    },

    normalizeFieldList (fields = [], sourceFields = []) {
      if (!Array.isArray(fields)) {
        return []
      }

      return fields
        .map((field) => {
          if (typeof field === 'string') {
            return sourceFields.find(item => item?.key === field) || null
          }

          return isObject(field)
            ? field
            : null
        })
        .filter(Boolean)
    },

    /**
     * Works a list of fields into what the template draws, one object per row.
     *
     * The value is resolved once here whichever way the row goes: a field drawn by a
     * cell component is handed it as `cellData`, and a plain one shows the same value
     * as text - so both come out of the single lookup rather than two.
     */
    buildFieldRows (fields, group) {
      return fields.map((field, index) => {
        const key = field?.key
        const cellData = this.getCellData(field)
        const cellType = this.getCellType(field)

        return {
          rowKey: `${group}_${key || field?.label || index}`,
          header: field,
          label: field?.label || key || DEFAULT_EMPTY_VALUE,
          // The same rule the table's rows go by: a column drawn by a component of its
          // own, or one the config gives a type to, is drawn by CellContent - and
          // everything else is written out as text. Reading the component alone was
          // what left a status column showing its raw value here while the table drew
          // the coloured pill.
          usesCellContent: !!field?.component || cellType !== PLAIN_CELL_TYPE,
          cellType,
          cellData,
          textValue: this.normalizeTextValue(cellData),
          headerCellConfig: this.getHeaderCellConfig(field)
        }
      })
    },

    getHeaderCellConfig (field) {
      return this.blockConfig?.headerCellConfig?.[field?.key] ||
        this.gridConfig?.headerCellConfig?.[field?.key] ||
        this.cellsConfig?.[field?.key] ||
        field ||
        {}
    },

    getCellType (field) {
      const key = field?.key
      const cellConfig = key ? (this.cellsConfig[key] || this.gridConfig?.cellsConfig?.[key] || {}) : {}

      return cellConfig.type
        ? cellConfig.type
        : PLAIN_CELL_TYPE
    },

    getCellData (field) {
      const key = field?.key

      if (!key) {
        return ''
      }

      const cellConfig = this.cellsConfig[key] || this.gridConfig?.cellsConfig?.[key] || {}
      const customDisplayValue = field?.custom_display_value || cellConfig.custom_display_value

      let cellData = !EMPTY_CELL_VALUES.includes(this.item?.[key]) ? this.item[key] : ''

      if (isFunction(customDisplayValue)) {
        cellData = customDisplayValue(this.item) || ''
      }

      return cellData
    },

    getFieldTextValue (field) {
      const value = this.getCellData(field)
      return this.normalizeTextValue(value)
    },

    normalizeTextValue (value) {
      if (value === null || value === undefined || value === '') {
        return DEFAULT_EMPTY_VALUE
      }

      if (typeof value === 'boolean') {
        return String(value)
      }

      if (Array.isArray(value)) {
        return value.length
          ? value.map(item => this.normalizeTextValue(item)).join(', ')
          : DEFAULT_EMPTY_VALUE
      }

      if (typeof value === 'object') {
        const objectValue = value?.name ?? value?.label ?? value?.description ?? value?.full_name ?? value?.title ?? value?.value
        if (objectValue === null || objectValue === undefined || objectValue === '') {
          return DEFAULT_EMPTY_VALUE
        }
        return objectValue
      }

      return value
    }
  }
}
</script>

<style scoped lang="scss">
.generic-grid-block {
  position: relative;
}

.generic-grid-block--clickable {
  cursor: pointer;
}

/* A ticked card, the colour the listing paints its picked rows in. The rounding is the
   column's - the card fills it, so a square colour would sit outside its corners. */
.generic-grid-block--selected {
  background-color: var(--selected-grid-card-color);
  border-radius: inherit;
}

/* Rows and cells of a card, laid out here rather than by `v-row` and `v-col`.
   They were a grid row and two grid columns each - and a card holds five of them, so a
   listing of a thousand records stood up tens of thousands of Vuetify components to
   draw what is a pair of divs. The measurements are the ones those components applied:
   a row that wraps with no margin of its own, cells padded by 12px. */
.generic-grid-block__row {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
}

.generic-grid-block__cell {
  flex: 1 0 0;
  max-width: 100%;
  padding: 12px;
  box-sizing: border-box;
  min-width: 0;
}

.generic-grid-block__cell--half {
  flex: 0 0 50%;
  max-width: 50%;
}

.generic-grid-block__header {
  border-bottom: 1px solid #E5E7EB;
}

.generic-grid-block__title {
  display: flex;
  justify-content: flex-start;
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #111827;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

/* The tick keeps its size whatever the title does - it shares a flex line with a
   heading that is allowed to run out of room and be clipped. */
.generic-grid-block__title ::v-deep .row-checkbox {
  flex: none;
}

.generic-grid-block__title-wrapper {
  display: block;
  height: 48px;
  line-height: 48px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.generic-grid-block__actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.data-label {
  font-family: 'Satoshi', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #6B7280;
  white-space: nowrap;
}

.data-value {
  display: flex;
  justify-content: flex-end;
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #111827;
  text-align: right;
}

.truncate-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Room at the foot for the arrow, which sits over the bottom of the card. */
.generic-grid-block__expansion {
  padding-bottom: 36px;
  cursor: default;
}

.arrowUpDown {
  position: absolute;
  bottom: 10px;
  left: 50%;
}

.arrowUpDown:hover {
  cursor: pointer;
}
</style>
